import { ActionKind } from '@dynamic/ui';
import { t } from 'i18next';
import { useLocation } from 'react-router-dom';

import { useActions, useNavigation } from '#/hooks';
import { AppStatus } from '#/service/constants';
import {
  isGeoRestrictionError,
  isPlaybackError,
  isUserAccessError,
  UserAccessErrorReason,
} from '#/service/error/errors';

import { StatusProps } from './Status';

const useFallback = (error?: Error): StatusProps => {
  const { pathname } = useLocation();
  const { goBack, reloadPage } = useNavigation('Fallback');
  const { actions } = useActions();

  const isFromPlayback = pathname.startsWith('/playback/');

  const commonProps = {
    status: AppStatus.AccessDenied,
    statusTitle: error?.name,
    statusMessage: error?.message,
  };

  switch (true) {
    // display player specific errors - now only from video player
    case isPlaybackError(error):
      return {
        ...commonProps,
        onDismiss: goBack,
        status: AppStatus.Playback,
        statusTitle: t('error.generic.title'),
      };
    // app is geoblocked - no dismiss because there's nowhere to go
    // playback is geoblocked - back button
    case isGeoRestrictionError(error):
      return {
        ...commonProps,
        status: AppStatus.GeoBlocked,
        ...(isFromPlayback && {
          dismissMessage: t('button.goBack'),
          onDismiss: goBack,
        }),
      };
    // authentication needed - login button
    case isUserAccessError(error, UserAccessErrorReason.AUTHENTICATION):
      return {
        ...commonProps,
        dismissMessage: t('button.login'),
        onDismiss: () => {
          actions[ActionKind.Login]();
        },
      };
    // entitlement needed - subscribe button
    case isUserAccessError(error, UserAccessErrorReason.ENTITLEMENT):
      return {
        ...commonProps,
        dismissMessage: t('button.subscribe'),
        onDismiss: () => {
          actions[ActionKind.Purchase]();
        },
      };
    // max concurrent playbacks reached - back button
    case isUserAccessError(error, UserAccessErrorReason.DEVICE_CONCURRENCY):
      return {
        ...commonProps,
        status: AppStatus.Error,
        onDismiss: goBack,
      };

    // any other user access error - reload button
    case isUserAccessError(error):
      return {
        ...commonProps,
        onDismiss: reloadPage,
      };
    default:
      return {
        fatalError: true,
        onDismiss: goBack,
      };
  }
};

export default useFallback;
